import type { RumEvent } from '@datadog/browser-rum'

import { datadogLogs } from '@datadog/browser-logs'
import {
  DATADOG_IGNORABLE,
  DATADOG_STATES,
} from '~~/layers/datadog/app/constants/datadog'

type GlobalMessageContextKeys = 'amount' | 'paymentId' | 'playSlipId'

export const DATADOG_LOG_TYPES = {
  Error: 'error',
  Info: 'info',
  Warn: 'warn',
} as const

export type DatadogLogType = ObjectValues<typeof DATADOG_LOG_TYPES>

export type DatadogPayload = {
  appendPlayerInfo?: boolean
  component?: string
  error?: unknown
  message: string
  messageContext?: LooseObject
}

export type IgnorableFn = (
  type: DatadogLogType | RumEvent['type'],
  message: string,
  error: DatadogPayload['error'],
) => boolean

export const useDatadog = () => {
  const {
    datadog: { enabled },
  } = useRuntimeConfig().public

  const globalMessageContext = useState<Map<GlobalMessageContextKeys, any>>(
    () => new Map(),
  )

  const playerId = useState<Maybe<string>>(DATADOG_STATES.Player, () => null)

  const addToGlobalContext = (key: GlobalMessageContextKeys, value: any) => {
    if (globalMessageContext.value.has(key)) return
    globalMessageContext.value.set(key, value)
  }

  const clearGlobalContext = () => {
    globalMessageContext.value.clear()
  }

  const log = (type: DatadogLogType, payload: DatadogPayload) => {
    if (shouldBeIgnored(type, payload)) return

    const {
      appendPlayerInfo,
      component,
      error,
      message: originalMessage,
      messageContext,
    } = payload

    const message = appendPlayerInfo
      ? `${originalMessage} for player ${playerId.value}`
      : originalMessage

    const composedMessageContext = {
      ...Object.fromEntries(globalMessageContext.value),
      ...(component ? { component } : {}),
      ...(messageContext || {}),
    }

    if (enabled) {
      customIdleCallback(() =>
        datadogLogs.logger[type](
          message,
          composedMessageContext,
          isError(error) ? error : undefined,
        ),
      )
      return
    }

    if (getIsDev()) {
      console[type]('[DATADOG-DEBUG]', {
        error,
        message,
        messageContext: composedMessageContext,
        timestamp: new Date().toISOString(),
      })
    }
  }

  const removeFromGlobalContext = (key: GlobalMessageContextKeys) => {
    if (!globalMessageContext.value.has(key)) return
    globalMessageContext.value.delete(key)
  }

  const shouldBeIgnored = (
    type: DatadogLogType,
    payload: DatadogPayload | string,
  ) => {
    const message = typeof payload === 'string' ? payload : payload.message
    const error = typeof payload === 'object' ? payload.error : undefined

    return DATADOG_IGNORABLE.some((fn) => fn(type, message, error))
  }

  return {
    addToGlobalContext,
    clearGlobalContext,
    log,
    removeFromGlobalContext,
    shouldBeIgnored,
  }
}
